import React from "react";
import {
  Box,
  Link,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CopyTypography } from "@rentablo/components";
import { useSharedPortfolioContext } from "@providers/SharedPortfolioProvider";
import { SecurityImage } from "@rentablo/components";
import { Link as RouterLink } from "react-router";
import { useIsinValidation } from "@hooks";
import ErrorIcon from "@icons/myicons/error-filled.svg?react";

const MOBILE_CLAMP_SX: SxProps<Theme> = {
  display: "-webkit-box",
  WebkitLineClamp: "3",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
};

type Props = {
  item: {
    id?: number | string;
    wkn?: string;
    type?: string;
    isin?: string;
    hasValidIsin?: boolean;
    standardisedName?: string;
    quoteProvider?: string;
    name?: string;
  };
  circleColor?: string;
  link?: string | null;
  onClick?: (e?: React.MouseEvent) => void;
  image?: string | null;
};

export const InvestmentInfo = ({
  item,
  link,
  onClick,
  image,
  circleColor,
}: Props) => {
  const { isSharedPortfolio } = useSharedPortfolioContext();
  const { isIsinRequired, isIsinValid } = useIsinValidation(item);
  const { t } = useTranslation();
  const name = item.standardisedName || item.name;
  const linkToGo =
    link === null ? undefined : link || `/investments/${item.id}`;
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  return (
    <Box display="flex" alignItems="center" gap={2} position="relative">
      <SecurityImage
        src={image}
        width={32}
        height={32}
        alt={name}
        circleColor={circleColor}
      />
      <Box minWidth={0}>
        <Typography
          variant="body3"
          {...(isSM
            ? { sx: MOBILE_CLAMP_SX }
            : { noWrap: true, maxWidth: 350 })}
        >
          {isSharedPortfolio || (!linkToGo && !onClick) ? (
            name
          ) : linkToGo ? (
            <Link component={RouterLink} to={linkToGo} onClick={onClick}>
              {name}
            </Link>
          ) : (
            <Link onClick={onClick}>{name}</Link>
          )}
        </Typography>

        <Box
          display="flex"
          flexDirection={isSM ? "column" : "row"}
          alignItems={isSM ? "flex-start" : "center"}
          columnGap={2}
          rowGap={0}
        >
          {item.type && (
            <Typography color="textSecondary" variant="body3">
              {t(
                `dashboardPage.dashboard.investmentsTable.types.${item.type}`,
                {
                  count: 1,
                }
              )}
            </Typography>
          )}
          {item.wkn && <CopyTypography>{item.wkn}</CopyTypography>}
          {item.hasValidIsin && item.isin && (
            <CopyTypography>{item.isin}</CopyTypography>
          )}
          {isIsinRequired && !isIsinValid && (
            <Typography
              component={RouterLink}
              to={`/investments/${item.id}/edit?tab=advanced`}
              variant="body3"
              color="error"
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <ErrorIcon width={16} height={16} style={{ marginRight: 4 }} />
              {t("investmentsPage.isinMissing")}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
